import { useMemo } from 'react';
import { useAtomValue } from 'jotai';

import { NOT_APPLICABLE_LABEL } from '@/constants';
import { formatEnumForDisplay } from '@/utils';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';

import {
  PointOfInterest,
  PointOfInterestTemplateField,
  PoiStatus,
  UnitsEnum,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { localiseMeasurement } from '@/utils/unitSystem';

type PaintBlockDataProps = {
  pointOfInterests: PointOfInterest[];
  columns: PointOfInterestTemplateField[];
};

type PoiRowProps = {
  pointOfInterests: PointOfInterest[];
  unitSystem: UnitSystemEnum;
  columns: PointOfInterestTemplateField[];
};

const generateRows = ({ pointOfInterests, unitSystem, columns }: PoiRowProps) => {
  return (pointOfInterests ?? [])
    .map((pointOfInterest) => {
      const { name, templateData, accessibility, id, status } = pointOfInterest;

      if (status === PoiStatus.Completed || status === PoiStatus.Remediated) return;

      const rowData: { [key: string]: string } = {
        'Chase Item': name || NOT_APPLICABLE_LABEL,
        Component: pointOfInterest.assembly?.tagName || '',
        'Accessibility requirement': formatEnumForDisplay(accessibility),
        id,
      };
      templateData?.forEach(({ id: fieldId, value: fieldValue }) => {
        if (!fieldId || !fieldValue) return;
        const fieldUnit = columns.find((item) => item.id === fieldId)?.unit;
        const fieldName = columns.find((item) => item.id === fieldId)?.name;
        if (!fieldName) return;
        rowData[fieldName] =
          fieldUnit === UnitsEnum.SquareMeter
            ? localiseMeasurement({
                unitSystem,
                sourceUnit: UnitsEnum.SquareMeter,
                value: Number(fieldValue),
              }) ?? ''
            : fieldValue;
      });
      return rowData;
    })
    .filter((row) => !!row);
};

export const usePaintBlockData = ({ pointOfInterests, columns }: PaintBlockDataProps) => {
  const unitSystem = useAtomValue(unitSystemState);

  const rows = useMemo(
    () => generateRows({ pointOfInterests, unitSystem, columns }).filter((row) => !!row),
    [pointOfInterests, unitSystem, columns]
  );
  return rows;
};
